import { Eye, Rocket, ShieldCheck, Sliders } from "lucide-react";
import { useTranslation } from "@thyra/landing-i18n";
import { TooltipCard } from "./ui/TooltipCard";
import { SectionHeader } from "./ui/SectionHeader";

const STACK_APPS = [
  { src: "/connections/gmail.svg", alt: "Gmail" },
  { src: "/connections/outlook.svg", alt: "Outlook" },
  {
    src: "/connections/googlecalendar.svg",
    altKey: "features.connect.calendarAlt",
  },
  { src: "/connections/linear.svg", alt: "Linear" },
  { src: "/connections/calcom.svg", alt: "Cal.com" },
  { src: "/connections/twilio.svg", alt: "Twilio" },
  { src: "/connections/slack.svg", alt: "Slack" },
] as const;

export function FeaturesBento() {
  const { t } = useTranslation("home");

  return (
    <section id="product" className="w-full py-20 sm:py-28 bg-background-muted">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={t("features.eyebrow")}
          title={t("features.title")}
          description={t("features.description")}
        />
        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2 marketing-card marketing-card-hover p-6">
            <div className="flex h-32 items-center justify-center rounded-lg bg-[var(--color-primary-muted)]">
              <div className="flex flex-wrap justify-center gap-3">
                {STACK_APPS.map((app) => (
                  <img
                    key={app.src}
                    src={app.src}
                    alt={"altKey" in app ? t(app.altKey) : app.alt}
                    className="h-10 w-10 object-contain opacity-90"
                  />
                ))}
              </div>
            </div>
            <TooltipCard
              content={
                <p className="text-muted-foreground">
                  {t("features.connect.tooltip")}
                </p>
              }
            >
              <h3 className="mt-4 text-lg font-semibold text-card-foreground cursor-help">
                {t("features.connect.title")}
              </h3>
            </TooltipCard>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("features.connect.body")}
            </p>
          </div>
          <div className="group/obs marketing-card marketing-card-hover p-6">
            <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-lg bg-muted">
              {/* Fog layer */}
              <span
                className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/70 via-neutral-200/40 to-neutral-300/30 dark:from-neutral-500/25 dark:via-neutral-600/15 dark:to-neutral-700/20"
                style={{
                  animation: "observability-fog 2.5s ease-in-out infinite",
                }}
                aria-hidden
              />
              <Eye
                className="relative h-12 w-12 text-muted-foreground transition-colors group-hover/obs:text-muted-foreground-strong"
                style={{
                  animation: "observability-scan 2.5s ease-in-out infinite",
                }}
              />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-card-foreground">
              {t("features.visibility.title")}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("features.visibility.body")}
            </p>
          </div>
          <div className="group/sec marketing-card marketing-card-hover p-6">
            <div className="relative flex h-24 items-center justify-center rounded-lg bg-muted">
              <span
                className="absolute rounded-full border-2 border-neutral-400/50 dark:border-neutral-500/50"
                style={{
                  width: 72,
                  height: 72,
                  animation: "secure-ring 2s ease-in-out infinite",
                }}
                aria-hidden
              />
              <ShieldCheck
                className="relative h-12 w-12 text-muted-foreground transition-colors group-hover/sec:text-muted-foreground-strong"
                style={{
                  animation: "secure-shield-pulse 2s ease-in-out infinite",
                }}
              />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-card-foreground">
              {t("features.control.title")}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("features.control.body")}
            </p>
          </div>
          <div className="sm:col-span-2 marketing-card marketing-card-hover p-6 flex flex-col sm:flex-row gap-4">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-muted">
              <Sliders className="h-12 w-12 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">
                {t("features.boundaries.title")}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("features.boundaries.body")}
              </p>
            </div>
          </div>
          <div className="group/mkt sm:col-span-2 marketing-card marketing-card-hover p-6 flex flex-col sm:flex-row gap-4">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-muted">
              <Rocket
                className="h-12 w-12 text-muted-foreground transition-colors group-hover/mkt:text-muted-foreground-strong"
                aria-hidden
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">
                {t("features.ready.title")}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("features.ready.body")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
